/**
 * Searchable Mixin
 *
 * Provides reusable search functionality for Vue components
 * Handles search state, query management, and text highlighting
 *
 * La búsqueda vive solo mientras el componente está montado: no se guarda en
 * sessionStorage ni se repone al volver a entrar al mapa, porque quedaba pegada
 * de una visita a la siguiente (QA-178).
 */

export const searchable = {
  data() {
    return {
      searchQuery: '',
      debouncedSearchQuery: '',      // The actual query used for filtering
      debounceTimer: null,            // Timer reference for cleanup
      debounceDelay: 300,             // Configurable delay in ms
      isDebouncing: false,            // UI feedback state
      searchResults: [],
      isSearching: false,
      searchHistory: []
    };
  },

  computed: {
    /**
     * Check if there's an active search query
     * Uses debouncedSearchQuery to prevent premature filtering
     */
    hasSearchQuery() {
      return this.debouncedSearchQuery.trim().length > 0;
    },

    /**
     * Get the count of search results
     */
    searchResultCount() {
      return this.searchResults.length;
    }
  },

  watch: {
    /**
     * Watch searchQuery and debounce updates to debouncedSearchQuery
     */
    searchQuery(newQuery) {
      // Clear existing timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // If query is empty, clear immediately (no debounce)
      if (!newQuery.trim()) {
        this.debouncedSearchQuery = '';
        this.isDebouncing = false;
        return;
      }

      // Set debouncing state
      this.isDebouncing = true;

      // Start new debounce timer
      this.debounceTimer = setTimeout(() => {
        this.debouncedSearchQuery = newQuery;
        this.isDebouncing = false;
      }, this.debounceDelay);
    }
  },

  methods: {
    /**
     * Perform search operation
     * Override this method in your component
     */
    performSearch() {
      console.warn('performSearch method should be overridden in component');
    },

    /**
     * Force immediate search (bypass debounce)
     * Used for Enter key and button clicks
     */
    forceSearch() {
      // Clear any pending debounce
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // Update immediately
      this.debouncedSearchQuery = this.searchQuery;
      this.isDebouncing = false;

      // Call the overridable performSearch for additional logic
      this.performSearch();
    },

    /**
     * Clear search query and results
     */
    clearSearch() {
      // El watcher de searchQuery vacía debouncedSearchQuery recién en el
      // siguiente tick, así que hay que limpiarlo acá: si no, un debounce en
      // vuelo alcanza a reponer el término que el usuario acaba de borrar.
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      this.searchQuery = '';
      this.debouncedSearchQuery = '';
      this.isDebouncing = false;
      this.searchResults = [];
    },

    /**
     * Highlight matched text in a string
     * @param {string} text - The text to search in
     * @param {string} query - The search query (defaults to this.searchQuery)
     * @returns {string} HTML string with highlighted matches
     */
    highlightMatch(text, query = null) {
      const searchTerm = query || this.searchQuery;
      if (!searchTerm || !text) return text;

      // Escape special regex characters in the search term
      const escapedQuery = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedQuery})`, 'gi');

      return text.replace(regex, '<mark class="search-highlight">$1</mark>');
    },

    /**
     * Check if a string matches the search query
     * @param {string} text - The text to check
     * @param {string} query - The search query (defaults to this.searchQuery)
     * @returns {boolean}
     */
    matchesSearch(text, query = null) {
      const searchTerm = query || this.searchQuery;
      if (!searchTerm || !text) return false;

      return text.toLowerCase().includes(searchTerm.toLowerCase());
    },

    /**
     * Add current search to history
     */
    addToSearchHistory() {
      if (this.searchQuery && !this.searchHistory.includes(this.searchQuery)) {
        this.searchHistory.unshift(this.searchQuery);
        // Keep only last 10 searches
        this.searchHistory = this.searchHistory.slice(0, 10);
      }
    }
  },

  beforeDestroy() {
    // Clean up timer on component destruction
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }
};
